import { useEventListener, useMergedRef } from '@fullstory/react-hooks';
import { classNames } from '@fullstory/util/fmt';
import React, { PropsWithChildren, useCallback, useRef } from 'react';
import { Box } from '../../legacy/Box';
import { Fade } from '../../legacy/Fade';
import { useOverlayPortal } from '../../legacy/Overlay';
import { identifyComponent } from '../../util';
import { ClickAwayListener } from '../ClickAwayListener';
import { usePortalPopper } from './hooks';
import css from './index.module.scss';
import { PopoverProps } from './types';

// Keys that close an open portal
export const portalCloseKeys = ['Escape'];

export const Popover = ({
  testId = 'popover',
  anchor,
  virtualAnchor,
  open,
  children,
  offset = 0,
  onCloseSignal,
  placement,
  arrow,
  variant = 'light',
  flipX,
  flipY,
  onFirstUpdate,
  portal = true,
  positioningStrategy,
  rootBoundary,
  borderRadius = 4,
  preventOverflow,
  ...boxProps
}: PropsWithChildren<PopoverProps>) => {
  const { popperRef, arrowRef, attributes, styles } = usePortalPopper({
    anchor,
    arrow,
    offset,
    placement,
    flipX,
    flipY,
    onFirstUpdate,
    virtualAnchor,
    positioningStrategy,
    rootBoundary,
    preventOverflow,
  });
  const fadeRef = useRef<HTMLDivElement>(null);
  const combined = useMergedRef(popperRef, fadeRef);
  const createPortal = useOverlayPortal();

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (portalCloseKeys.includes(e.key)) onCloseSignal?.();
    },
    [onCloseSignal],
  );

  const onClickAway = useCallback(() => onCloseSignal?.(), [onCloseSignal]);

  // Add event listeners
  useEventListener({
    name: 'keydown',
    handler: handleKeyDown,
    attached: open,
  });

  const popover = (
    <Fade in={open} nodeRef={fadeRef}>
      <ClickAwayListener anchor={anchor} onClickAway={onClickAway}>
        <Box
          ref={combined}
          role="dialog"
          testId={testId}
          className={classNames(css.tooltip, css[variant])}
          style={{ ...styles.popper, borderRadius }}
          {...boxProps}
          {...identifyComponent('popover', boxProps)}
          {...attributes.popper}
        >
          {children}
          {arrow && variant !== 'bordered' && variant !== 'bordered-brand' && (
            <div
              data-testid="popover-arrow"
              ref={arrowRef}
              style={styles.arrow}
              className={css.arrow}
            />
          )}
        </Box>
      </ClickAwayListener>
    </Fade>
  );

  return portal ? createPortal(popover) : popover;
};
